import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Alert,
    AlertIcon,
    Button,
    Center,
    Container,
    Heading,
    Link,
    Stack,
    Text
} from '@chakra-ui/react';
import { SocialLogin } from "frontend/social/components";
import { AnvilInput } from "frontend/form/components";
import { useAnvilProps } from "frontend/hooks";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { coreUrls } from "core_src/js/core_urls_config";
import { socialConfig } from "anvil_social_src/social_config";
import { analytics, anvilAnalyticsConfig } from "anvil-analytics/analytics";
import axios from "axios";
import { NewsletterSelection } from "frontend/account/components";
import { AccountHelpBox } from "frontend/auth/components";
import { RichText } from "frontend/wagtail/components/RichText";
import { ColorButton } from "../../core/components/ColorButton";


/**
 * Show social login options, an anvil password form and a newsletter signup for users needing to register.
 *
 * Valid props:
 *  - userEmail: The email address that the account is tied too.
 */
function NewAccount(props) {
    const {
        userEmail,
    } = props;

    const [showNewslettersHeader, setShowNewslettersHeader] = useState(false);

    const {formState: {isSubmitting}} = useFormContext();

    const socialNextUrl = `${window.location.pathname}#/thanks`;

    useEffect(() => {
        analytics.track('ShowOrderTunnelRegistration');
    }, []);

    const loadedNewsletterCallback = useCallback((newsletters) => {
        setShowNewslettersHeader(newsletters && newsletters.length !== 0)
    }, []);

    return (
        <>
            <Heading size="md">Create your account</Heading>
            {(socialConfig.apple.enabled || socialConfig.facebook.enabled) &&
                <Stack spacing="4">
                    <Heading size="sm">Using your social account</Heading>
                    <SocialLogin next={socialNextUrl}/>
                </Stack>
            }
            <Stack spacing="4">
                <Heading size="sm">Using your email</Heading>
                <AnvilInput name="email" isDisabled={true} labelText="Email address" defaultValue={userEmail}/>
                <AnvilInput name="password" isRequired={true} labelText="Password" inputProps={{
                    type: 'password',
                }}/>
                <AnvilInput name="password-confirm" isRequired={true} labelText="Password confirmation" inputProps={{
                    type: 'password',
                }}/>
            </Stack>
            {showNewslettersHeader && <Heading size="sm">Email preferences</Heading>}
            <NewsletterSelection loadedNewsletterCallback={loadedNewsletterCallback} />
            <Button type="submit" isLoading={isSubmitting} colorScheme="blue">Register</Button>
        </>
    );
}

/**
 * Show options for allowing a user to sign in to their account.
 *
 * Valid props:
 *  - userHasSocialAccount: Whether or not the user account is tied to a social account. If true, social login options
 *      will be shown.
 *  - userHasAnvilAccount: Whether or not the user has an Anvil account with a usable password. If true, shows a login
 *      form with a password input.
 *  - userEmail: The email the user is trying to authenticate for. This is the email tied to the account that
 *      the subscription was created for.
 */
function ExistingAccount(props) {
    const {
        userHasSocialAccount,
        userEmail,
        userHasUsablePassword,
        socialProviders = [],
    } = props;

    const {formState: {isSubmitting}} = useFormContext();

    const orderTunnelReturnUri = encodeURIComponent(`${window.location.pathname}${window.location.hash}`);
    const forgotPasswordUri = `${coreUrls.password_reset}?next=${orderTunnelReturnUri}`;

    const hasFacebook = useMemo(() => socialProviders.indexOf('facebook') !== -1, [socialProviders]);
    const hasApple = useMemo(() => socialProviders.indexOf('apple-id') !== -1, [socialProviders]);

    const socialNextUrl = `${window.location.pathname}#/thanks`;

    return (
        <>
            <Heading size="md">Log in to your account</Heading>
            {userHasSocialAccount && (
                <Stack spacing="4">
                    {userHasUsablePassword && <Heading size="sm">Using your social account</Heading>}
                    <SocialLogin next={socialNextUrl} showApple={hasApple} showFacebook={hasFacebook}/>
                    {!userHasUsablePassword && (
                        <Text>
                            If you are unable to login with your social account or would like to set a password
                            for your account then please visit our
                            {' '}
                            <Link href={forgotPasswordUri}>password reset page</Link>.
                        </Text>
                    )}
                </Stack>
            )}
            {userHasUsablePassword && (
                <Stack spacing="4">
                    {userHasSocialAccount && <Heading size="sm">Using your email</Heading>}
                    <AnvilInput name="email" isDisabled={true} labelText="Email address" defaultValue={userEmail}/>
                    <AnvilInput name="password" isRequired={true} labelText="Password" inputProps={{
                        type: 'password'
                    }}/>
                    <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>Login</Button>
                    <Link href={forgotPasswordUri}>Forgot password?</Link>
                </Stack>
            )}
            {!userHasUsablePassword && !userHasSocialAccount && (
                <Stack spacing={4}>
                    <Text>
                        It looks like there&apos;s no password set for your account.
                        Use the button below to set up your password and take advantage of your subscription.
                    </Text>
                    <ColorButton
                        as={"a"}
                        buttonColor="blue.500"
                        href={forgotPasswordUri}
                    >Reset Password</ColorButton>
                </Stack>
            )}
        </>
    );
}

const existingUserSchema = yup.object().shape({
    password: yup.string().required().label('Password'),
});

const newUserSchema = yup.object().shape({
    password: yup.string().required().label('Password'),
    'password-confirm': yup.string().oneOf(
        [yup.ref('password')],
        'Passwords must match',
    ).required().label('Confirm Password'),
});

/**
 * Wrapper for the AuthScreenContents to validate the location state before showing the AuthScreen.
 * Necessary to allow returning null early while maintaining consistency with react hooks.
 */
export default function AuthScreen() {
    const history = useHistory();

    const {
        state,
    } = useLocation();

    if (!state) {
        console.error('No location state found. Redirecting back to the previous screen.');
        history.goBack();
        return null;
    }

    return <AuthScreenContents/>;
}


/**
 * Show the user options to either login or create an account.
 */
export function AuthScreenContents() {

    const history = useHistory();

    const {
        state,
        pathname
    } = useLocation();

    const {
        email: userEmail,
        firstName,
        lastName,
        hasExistingAccount,
        hasUsablePassword,
        socialProviders,
        socialAlreadyAssociated,
    } = state;

    const userHasSocialAccount = useMemo(() => (socialProviders && socialProviders.length > 0), [socialProviders]);

    const [formError, setFormError] = useState('');

    useEffect(() => {
        analytics.page(anvilAnalyticsConfig.page_payload);
    }, [])


    useEffect(() => {
        if (socialAlreadyAssociated) {
            setFormError(
                'Unable to login using your social account. The social login you used is already connected to ' +
                'an existing account.'
            )
        }
    }, [socialAlreadyAssociated]);

    const {messages} = useAnvilProps();
    const headerText = hasExistingAccount ? messages.account_login_header : messages.account_creation_header;
    const bodyText = hasExistingAccount ? messages.account_login_body : messages.account_creation_body;

    const form = useForm({
        resolver: yupResolver(hasExistingAccount ? existingUserSchema : newUserSchema),
    });

    const {
        handleSubmit,
    } = form;

    // Handle submitting the form
    // Will attempt to login in existing users, and will set the password for new users
    const handleSubmission = useCallback(async (data) => {
        if (hasExistingAccount) {
            data['email'] = userEmail;
            try {
                await axios.post(coreUrls.login_api, data);
                analytics.track('CompleteLogin', {
                    page: pathname,
                    login_method: "Direct",
                    is_order_tunnel: true,
                })
            } catch (e) {
                console.error(e);
                if (e.response && e.response.status === 404) {
                    setFormError(e.response.data.detail);
                    return;
                }
                setFormError('Unable to login. Please try again.');
                return;
            }
            // Just logged in an existing user, update the user's first & last names if they are not already set.
            try {
                const response = await axios.get(coreUrls.current_user_api);  // Get the SwagUser's saved data.
                const {
                    first_name: currentFirstName,
                    last_name: currentLastName
                } = response.data

                if (!(currentFirstName && currentLastName) && firstName && lastName) {
                    axios.patch(coreUrls.current_user_api, {
                        'first_name': firstName,
                        'last_name': lastName,
                    });
                }
            } catch (e) {
                console.error("Unable to update the user's first & last name information.")
            }
        }
        // Handle setting the password for new accounts
        else {
            try {
                await axios.patch(coreUrls.current_user_api, {
                    'password': data['password'],
                });
                analytics.track('CompleteRegister', {
                    page: pathname,
                    is_order_tunnel: true,
                    login_method: "Direct"
                })
            } catch (e) {
                console.error(e);
                setFormError('Unable to set a password for your account. Please try again.');
                return;
            }
        }

        // Success, so take the user to the thanks screen
        history.push('/thanks');
    }, [firstName, hasExistingAccount, history, lastName, pathname, userEmail]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(handleSubmission)}>
                <Container maxW="4xl">
                    <Alert status="success" variant="solid-rounded">
                        <AlertIcon/>
                        Your order is complete!
                    </Alert>
                    <Heading mt="4" size="md">{headerText}</Heading>
                    <RichText mt="4" data={bodyText} />
                    <Center mt="8">
                        <Stack spacing="5" w="full" maxW="lg">
                            {!!formError && (
                                <Alert status="error" variant="subtle">
                                    <AlertIcon/>
                                    {formError}
                                </Alert>
                            )}
                            {hasExistingAccount && (
                                <ExistingAccount userEmail={userEmail}
                                                 userHasSocialAccount={userHasSocialAccount}
                                                 userHasUsablePassword={hasUsablePassword}
                                                 socialProviders={socialProviders}
                                />
                            )}
                            {!hasExistingAccount && <NewAccount userEmail={userEmail}/>}
                            <AccountHelpBox/>
                        </Stack>
                    </Center>
                </Container>
            </form>
        </FormProvider>
    );
}
